import type { TrendingDestinationWithId } from 'home/components/OfferCarousels/localDestinationOfferCarouselUtils'

export function isLocalDestinationOfferListEligibilityResolved(
  list: App.OfferList | undefined,
): list is App.OfferList {
  return !!list && !list.fetching && !list.error
}

export function isLocalDestinationOfferListEligibilityComplete(
  list: App.OfferList | undefined,
  retriesExhausted = false,
): boolean {
  if (!list || list.fetching) {
    return false
  }

  if (!list.error) {
    return true
  }

  return retriesExhausted
}

export type LocalDestinationOfferCarouselEligibilityOptions = {
  exhaustedListKeys?: ReadonlySet<string>
  listKeys?: Array<string>
}

export function selectLocalDestinationOfferCarouselState(
  destinationsWithIds: Array<TrendingDestinationWithId>,
  offerListStates: Array<App.OfferList | undefined>,
  options: LocalDestinationOfferCarouselEligibilityOptions = {},
) {
  const { exhaustedListKeys, listKeys } = options

  const isEligibilityComplete = (index: number) => {
    const list = offerListStates[index]
    const listKey = listKeys?.[index]
    const retriesExhausted = !!listKey && !!exhaustedListKeys?.has(listKey)

    return isLocalDestinationOfferListEligibilityComplete(list, retriesExhausted)
  }

  const eligibleDestinations = destinationsWithIds.filter((destination, index) => {
    const list = offerListStates[index]
    return isLocalDestinationOfferListEligibilityResolved(list) && list.offerIds.length > 0
  })

  const allChecksComplete = destinationsWithIds.length === 0 || destinationsWithIds.every((_, index) => {
    return isEligibilityComplete(index)
  })

  // Skeleton hides once the first eligible destination resolves; remaining chips may drip in later.
  const checkingEligibility = eligibleDestinations.length === 0 && !allChecksComplete

  return {
    eligibleDestinations,
    checkingEligibility,
    allChecksComplete,
  }
}

export type LocalDestinationOfferCarouselEligibilityState = ReturnType<typeof selectLocalDestinationOfferCarouselState>

/**
 * End the eligibility skeleton once the eligibility timeout elapses. Does not force
 * `allChecksComplete` or discard late offers; see `LOCAL_DESTINATION_OFFER_ELIGIBILITY_TIMEOUT_MS`
 * for the possible post-timeout carousel insertion / CLS trade-off.
 */
export function applyLocalDestinationOfferCarouselEligibilityTimeout(
  state: LocalDestinationOfferCarouselEligibilityState,
  eligibilityTimedOut: boolean,
): LocalDestinationOfferCarouselEligibilityState {
  if (!eligibilityTimedOut) {
    return state
  }

  return {
    ...state,
    checkingEligibility: false,
  }
}

export function shouldShowLocalDestinationOfferCarousel(options: {
  isHotelFilter: boolean
  isEnabled: boolean
  fetchingDestinations: boolean
  destinationCount: number
}): boolean {
  const { isHotelFilter, isEnabled, fetchingDestinations, destinationCount } = options

  return isHotelFilter && isEnabled && !fetchingDestinations && destinationCount > 0
}

export function isLocalDestinationOfferCarouselMarketEnabled(regionCode: string, enabledRegions: Set<string>): boolean {
  return enabledRegions.has(regionCode)
}

export function isLocalDestinationOfferCarouselEnabled(
  regionCode: string,
  enabledRegions: Set<string>,
  featureFlagEnabled: boolean,
): boolean {
  return featureFlagEnabled && isLocalDestinationOfferCarouselMarketEnabled(regionCode, enabledRegions)
}

/** Hide only after every destination resolved with zero eligible offers — not while still checking */
export function shouldRenderLocalDestinationOfferCarouselBody(options: {
  defaultDestinationId: string | undefined
  allChecksComplete: boolean
  eligibleDestinationCount: number
}): boolean {
  const { defaultDestinationId, allChecksComplete, eligibleDestinationCount } = options

  return !!defaultDestinationId && !(allChecksComplete && eligibleDestinationCount === 0)
}
